import {
  Alert,
  AlertActions,
  AlertContent,
  Button,
  Icon,
  IconButton,
  Offscreen,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TooltipTabstop
} from '@deque/cauldron-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SecurityEventLogExport } from '~/common/utils/admin-client/get-security-event-log-exports';
import styles from './SecurityLogExportsTable.css';
import { formatDate, formatFileSize, getExportStatusLabel } from './utils';

const NotAvailable: React.FC = () => {
  const { t } = useTranslation();
  return (
    <>
      <span className={styles.muted} aria-hidden="true">
        &mdash;
      </span>
      <Offscreen>{t('Not yet available')}</Offscreen>
    </>
  );
};

interface SecurityLogExportsTableProps {
  exports: SecurityEventLogExport[];
  onDownload: (exp: SecurityEventLogExport) => void;
  /**
   * Invoked after the user confirms deletion. Must resolve (not reject) even
   * when the deletion fails — the caller owns error surfacing; the table only
   * closes the confirmation once the attempt settles.
   */
  onDelete: (exp: SecurityEventLogExport) => Promise<void>;
}

const SecurityLogExportsTable: React.FC<SecurityLogExportsTableProps> = ({
  exports,
  onDownload,
  onDelete
}) => {
  const { t } = useTranslation();
  const [confirmingDelete, setConfirmingDelete] =
    useState<SecurityEventLogExport | null>(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  const handleConfirmDelete = async () => {
    if (!confirmingDelete) {
      return;
    }
    setDeleteInProgress(true);
    try {
      await onDelete(confirmingDelete);
    } finally {
      setDeleteInProgress(false);
      setConfirmingDelete(null);
    }
  };

  return (
    <>
      <div
        className={styles.scrollContainer}
        role="region"
        aria-label={t('Security log exports')}
        tabIndex={0}
      >
        <Table variant="border">
          <TableHead>
            <TableRow>
              <TableHeader scope="col">{t('Created')}</TableHeader>
              <TableHeader scope="col">{t('Date Range')}</TableHeader>
              <TableHeader scope="col">{t('Status')}</TableHeader>
              <TableHeader scope="col">{t('Size')}</TableHeader>
              <TableHeader scope="col">{t('Events')}</TableHeader>
              <TableHeader scope="col">{t('Actions')}</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {exports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <div className={styles.empty}>
                    {t('No exports for this enterprise yet.')}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              exports.map(exp => (
                <TableRow key={exp.exportId}>
                  <TableCell>{formatDate(exp.createdAt)}</TableCell>
                  <TableCell>
                    {t('{{startDate}} to {{endDate}}', {
                      startDate: exp.startDate,
                      endDate: exp.endDate
                    })}
                  </TableCell>
                  <TableCell>
                    <span className={styles.status}>
                      {getExportStatusLabel(t, exp.status)}
                    </span>
                    {exp.status === 'failed' && exp.error && (
                      <TooltipTabstop
                        className={styles.failureDetails}
                        association="aria-describedby"
                        tooltip={exp.error}
                      >
                        <Icon type="info-circle" label={t('Failure details')} />
                      </TooltipTabstop>
                    )}
                  </TableCell>
                  <TableCell>
                    {exp.fileSize === null ? (
                      <NotAvailable />
                    ) : (
                      formatFileSize(exp.fileSize)
                    )}
                  </TableCell>
                  <TableCell>
                    {exp.eventCount === null ? (
                      <NotAvailable />
                    ) : (
                      exp.eventCount.toLocaleString()
                    )}
                  </TableCell>
                  <TableCell className={styles.actions}>
                    <IconButton
                      icon="download"
                      label={t('Download export {{startDate}} to {{endDate}}', {
                        startDate: exp.startDate,
                        endDate: exp.endDate
                      })}
                      disabled={exp.status !== 'completed'}
                      onClick={() => onDownload(exp)}
                    />
                    <IconButton
                      icon="trash"
                      label={t('Delete export {{startDate}} to {{endDate}}', {
                        startDate: exp.startDate,
                        endDate: exp.endDate
                      })}
                      onClick={() => setConfirmingDelete(exp)}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Alert
        variant="warning"
        heading={t('Delete Export')}
        show={!!confirmingDelete}
        onClose={() => setConfirmingDelete(null)}
      >
        <AlertContent>
          {confirmingDelete &&
            t(
              'Are you sure you want to delete the export for {{startDate}} to {{endDate}}? The export file will be permanently deleted.',
              {
                startDate: confirmingDelete.startDate,
                endDate: confirmingDelete.endDate
              }
            )}
        </AlertContent>
        <AlertActions>
          <Button
            variant="error"
            disabled={deleteInProgress}
            onClick={handleConfirmDelete}
          >
            {t('Delete')}
          </Button>
          <Button
            variant="secondary"
            disabled={deleteInProgress}
            onClick={() => setConfirmingDelete(null)}
          >
            {t('Cancel')}
          </Button>
        </AlertActions>
      </Alert>
    </>
  );
};

export default SecurityLogExportsTable;
